import { Component, effect, inject, signal, untracked } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonCard,
  IonContent,
  IonHeader,
  IonIcon,
  IonLabel,
  IonSegment,
  IonSegmentButton,
  IonText,
  IonTitle,
  IonToolbar,
} from '@ionic/angular/standalone';
import { VxCurrencyPipe } from '@libs/core/lib/pipes/mtjr-currency.pipe';

import { DatePipe, NgStyle } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ApiService } from '@libs/core/base-config';
import { VxErrorBarComponent } from '@libs/core/lib/error-view/error-bar.component';
import { VxErrorComponent } from '@libs/core/lib/error-view/error-view.component';
import { NgEventBus } from '@libs/core/lib/event-bus/ng-event-bus';
import { VxSubmitButtonComponent } from '@libs/core/lib/form-controls/mtjr-submit-button.component';
import { LangSelectPipe } from '@libs/core/lib/pipes/lang-select.pipe';
import { VxLoadingComponent } from '@libs/core/lib/shared/loading-state';
import { VxImageComponent } from '@libs/core/lib/shared/mtjr-image.component';
import { asResult, dataFromState, loadingState } from '@libs/core/lib/state/async-states';
import { AppAlerts } from '@libs/core/lib/utils/app-alerts';
import {
  withGetOrderById,
  withMarkOrderAsProcessing,
  withMarkOrderAsReadyForShipping,
} from '@libs/mtjr/shared/generated/entities/order/order-store-features';
import { getOrderById } from '@libs/mtjr/shared/generated/entities/order/service/order.service';
import { Entity } from '@libs/mtjr/shared/generated/types';
import { OrderChatPage } from '@libs/mtjr/shared/orders/order-chat.page';
import { OrderStatusTextComponent } from '@libs/mtjr/shared/orders/order-status-text.component';
import { patchState, signalStore, withComputed, withMethods } from '@ngrx/signals';
import { TranslatePipe } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

export const OrderDetailsStore = signalStore(
  withGetOrderById(),
  withMarkOrderAsProcessing(),
  withMarkOrderAsReadyForShipping(),
  withMethods((store, apiService = inject(ApiService)) => ({
    async refreshOrder(orderId: string) {
      const result = await asResult(getOrderById(apiService, { id: orderId }));
      patchState(store, { getOrderByIdState: result });
    },
    async findOneVendorOrder(orderId: string, subOrderId: string) {
      patchState(store, { getOrderByIdState: loadingState() });
      const result = await asResult(getOrderById(apiService, { id: orderId }));
      if (result.success) {
        result.data.subOrders = result.data.subOrders.filter((subOrder) => subOrder.id === subOrderId);
      }
      patchState(store, { getOrderByIdState: result });
    },
  })),
  withComputed((store) => ({
    subOrder: dataFromState(store.getOrderByIdState, (data) => data?.subOrders?.[0]),
    order: dataFromState(store.getOrderByIdState, (data) => data),
  })),
);
@Component({
  selector: 'mav-orders',
  providers: [OrderDetailsStore],
  imports: [
    IonButton,
    IonIcon,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonButtons,
    IonBackButton,
    IonContent,
    IonCard,
    IonText,
    IonLabel,
    VxCurrencyPipe,
    VxLoadingComponent,
    VxErrorComponent,
    DatePipe,
    VxImageComponent,
    VxErrorBarComponent,
    VxSubmitButtonComponent,
    IonSegment,
    OrderChatPage,
    IonSegmentButton,
    OrderStatusTextComponent,
    NgStyle,
    DatePipe,
    TranslatePipe,
    IonIcon,
    FormsModule,
    RouterLink,
    ReactiveFormsModule,
    LangSelectPipe,
  ],
  templateUrl: './order-details.page.html',
})
export class OrderDetailsPage {
  tabs = ['details', 'chat'];
  store = inject(OrderDetailsStore);
  route = inject(ActivatedRoute);
  appAlerts = inject(AppAlerts);
  queryTab = this.route.snapshot.queryParamMap.get('tab');
  tab = signal(this.queryTab && this.tabs.includes(this.queryTab) ? this.queryTab : 'details');
  orderId: string = this.route.snapshot.params['orderId'];
  subOrderId: string = this.route.snapshot.params['subOrderId'];
  routeSub?: Subscription;
  router = inject(Router);

  eventBusSub?: Subscription;
  eventBusChatSub?: Subscription;
  eventBus = inject(NgEventBus);
  productPlaceholder = 'https://nbg1.your-objectstorage.com/mtjr-prod/product-placeholder.webp';

  constructor() {
    this.store.findOneVendorOrder(this.orderId, this.subOrderId);

    effect(() => {
      if (this.store.markOrderAsProcessingState().success) {
        untracked(() => this.store.getOrderById({ id: this.orderId }));
        this.appAlerts.successSnackbarTR('ORDERS.MARK_PROCESSING_SUCCESS');
      }
    });

    effect(() => {
      if (this.store.markOrderAsReadyForShippingState().success) {
        untracked(() => this.store.getOrderById({ id: this.orderId }));
        this.appAlerts.successSnackbarTR('ORDERS.MARK_SHIPPED_SUCCESS');
      }
    });

    effect(() => {
      if (this.store.markOrderAsProcessingState().success) {
        untracked(() => this.store.getOrderById({ id: this.orderId }));
        this.appAlerts.successSnackbarTR('ORDERS.MARK_DELIVERED_SUCCESS');
      }
    });

    this.eventBusSub = this.eventBus.on<{ orderId: string }>('order:fetch').subscribe((event) => {
      const orderId = event.data?.orderId;
      if (orderId && orderId === this.orderId) {
        this.store.refreshOrder(orderId);
      }
    });

    this.routeSub = this.route.queryParams.subscribe((params) => {
      const tab = params['tab'];
      if (tab) {
        this.tab.set(tab);
      }
    });
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: this.tab() },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  onSegmentChanged(event: CustomEvent) {
    this.tab.set(event.detail.value);
    this.router.navigate([], { relativeTo: this.route, queryParams: { tab: event.detail.value } });
  }

  productImage(item: Entity.OrderProduct) {
    return item.productImage ?? item.variationImage ?? this.productPlaceholder;
  }

  variationAttributes(item: Entity.OrderProduct): Entity.VariationAttribute[] {
    return Array.isArray(item.variationAttributes) ? item.variationAttributes : [];
  }

  async markProcessing(order: Entity.Order) {
    const confirm = await this.appAlerts.yesNoAlertTR({
      message: 'ORDERS.MARK_PROCESSING_CONFIRM',
      header: 'ORDERS.MARK_PROCESSING_CONFIRM_HEADING',
      yesCaption: 'COMMON.CONFIRM',
    });
    if (confirm) {
      this.store.markOrderAsProcessing({ orderId: order.id, subOrderId: order.subOrders[0].id });
    }
  }

  async markShipped(order: Entity.Order) {
    const confirm = await this.appAlerts.yesNoAlertTR({
      message: 'ORDERS.MARK_SHIPPED_CONFIRM',
      header: 'ORDERS.MARK_SHIPPED_CONFIRM_HEADING',
      yesCaption: 'COMMON.CONFIRM',
    });
    if (confirm) {
      this.store.markOrderAsReadyForShipping({ orderId: order.id, subOrderId: order.subOrders[0].id });
    }
  }

  async markAsProcessing(order: Entity.Order) {
    const confirm = await this.appAlerts.yesNoAlertTR({
      message: 'ORDERS.MARK_DELIVERED_CONFIRM',
      header: 'ORDERS.MARK_DELIVERED_CONFIRM_HEADING',
      yesCaption: 'COMMON.CONFIRM',
    });
    if (confirm) {
      this.store.markOrderAsProcessing({ orderId: order.id, subOrderId: order.subOrders[0].id });
    }
  }
}
